import { Injectable } from '@angular/core';
import { IntlService, NumberPipe } from '@progress/kendo-angular-intl';
import { ReferenceDataCategoriesConstants } from '../constants/reference-data-categories';
import { ReferenceDataConstants } from '../constants/reference-data-constants';
import { ReferenceDataOptionLists } from '../constants/reference-data-option-lists';
import { ReferenceData } from '../models/reference-data.model';
import { ValidationError } from '../models/validation/validation-error';
import { NavigationItem } from '../models/navigation-item.model';

@Injectable({
  providedIn: 'root'
})
export class ReferenceDataService {
  public mapKeySeparator = '##';

  private readonly requiredCode = 'R';
  private readonly boundariesErrorPrefix = 'BE';
  private _referenceData: Map<string, Array<ReferenceData>>;
  private readonly referenceDataOptionLists = ReferenceDataOptionLists;

  constructor(private readonly intlService: IntlService) {}

  public static formatStringWithArguments(value: string, args: Array<string>): string {
    let replacedValue = value;
    if (value && args.length) {
      args.forEach(
        (arg, index) => (replacedValue = replacedValue.replace(`{${index}}`, arg).replace(`{${index}nr}`, arg))
      );
    }

    return replacedValue;
  }

  get referenceData(): any {
    return this._referenceData;
  }

  set referenceData(newReferenceData: any) {
    this._referenceData = new Map<string, Array<ReferenceData>>();
    newReferenceData.ReferenceData.forEach((configSection) => {
      const itemCategory = configSection.Category;
      configSection.Items.forEach((configurationItem) => {
        const itemId = configurationItem.Name;
        const itemValue = configurationItem.Items.map((refData) => new ReferenceData(refData));
        this.setConfiguration(itemCategory, itemId, itemValue);
      });
    });
  }

  private setConfiguration(category: string, id: string, value: Array<ReferenceData>): void {
    this._referenceData.set(`${category}${this.mapKeySeparator}${id}`, value);
  }

  private getConfiguration(category: string, name: string): Array<ReferenceData> {
    return this._referenceData.get(`${category}${this.mapKeySeparator}${name}`);
  }

  public getConfigurationValue(category: string, name: string): string {
    const config = this.getConfiguration(category, name);

    return config && config[config.length - 1].Value;
  }

  public getConfigurationValueByCode(category: string, name: string, code: string): string {
    const referenceDataArray = this.getConfiguration(category, name);

    if (referenceDataArray) {
      const referenceData = referenceDataArray.find((refData) => refData.Code === code);

      return referenceData ? referenceData.Value : undefined;
    }

    return undefined;
  }

  public getClientConfigurationValue(name: string): string {
    return this.getConfigurationValue(ReferenceDataCategoriesConstants.CLIENT_ENVIRONMENT_CATEGORY, name);
  }

  public getOptionListValueByCode(name: string, code: string): string {
    const referenceData = this.getConfiguration(ReferenceDataCategoriesConstants.OPTION_LIST_CATEGORY, name).find(
      (refData) => refData.Code === code
    );

    return referenceData ? referenceData.Value : code;
  }

  public isFieldRequired(id: string): boolean {
    let value: Array<ReferenceData> = [];

    ReferenceDataCategoriesConstants.INPUT_LABEL_CATEGORIES.some((category) => {
      const configuration = this.getConfiguration(category, id);
      if (configuration) {
        value = configuration;

        return true;
      }
    });

    return value.length > 0 && value[value.length - 1].Code === this.requiredCode;
  }

  public getApplicationCurrentVersion(): string {
    return this.getClientConfigurationValue(ReferenceDataConstants.CURRENT_APPLICATION_VERSION);
  }

  public getNewFeaturesConfigItems(name: string): Array<ReferenceData> {
    return this.getConfiguration(ReferenceDataCategoriesConstants.NEW_FEATURES, name);
  }

  public getOptionListConfigItems(name: string): Array<ReferenceData> {
    let values: Array<ReferenceData> = [];
    ReferenceDataCategoriesConstants.OPTION_LIST_CATEGORIES.some((category) => {
      const optionListValues = this.getConfiguration(category, name);
      if (optionListValues) {
        values = optionListValues;

        return true;
      }
    });

    return values;
  }

  public getNavigationConfigItems(name: string): Array<NavigationItem> {
    return this.getOptionListConfigItems(name)
      .sort((a, b) => +a.SortOrder - +b.SortOrder)
      .map((a) => new NavigationItem(a));
  }

  public getFilteredOptionListConfigItems(name: string, excludeAttribute: string): Array<ReferenceData> {
    let values = this.getOptionListConfigItems(name);

    if (excludeAttribute) {
      values = values.filter(
        (data) => data.getAttributeValue(excludeAttribute) !== this.referenceDataOptionLists.AnswerTypeYes
      );
    }

    return values;
  }

  public getOptionListConfigItemByCode(name: string, code: string): ReferenceData {
    const refDataArray = this.getOptionListConfigItems(name);

    return refDataArray && refDataArray.find((refData) => refData.Code === code);
  }

  public getLocalizeLabel(id: string): string {
    let value: string;
    ReferenceDataCategoriesConstants.LABEL_CATEGORIES.some((category) => {
      const labelValue = this.getConfigurationValue(category, id);
      if (labelValue !== undefined) {
        value = labelValue;

        return true;
      }
    });

    return value;
  }

  public getLocalizedContextualHelp(id: string): string {
    return this.getConfigurationValue(ReferenceDataCategoriesConstants.CONTEXTUAL_HELP, id);
  }

  public getLocalizeTooltip(id: string): string {
    return this.getConfigurationValue(ReferenceDataCategoriesConstants.TOOLTIP, id);
  }

  public getLocalizeErrorMessage(validationError: ValidationError): string {
    let errorMessage = this.getConfiguration(ReferenceDataCategoriesConstants.ERROR_MESSAGE, validationError.name).find(
      (error) => error.Code === validationError.code
    );
    let formattedMessage = '';

    if (
      validationError.name &&
      validationError.name.indexOf(this.boundariesErrorPrefix) === 0 &&
      validationError.args
    ) {
      const numberPipe = new NumberPipe(this.intlService);
      for (let i = 0; i < validationError.args.length; i++) {
        validationError.args[i] = isNaN(+validationError.args[i])
          ? validationError.args[i]
          : numberPipe.transform(validationError.args[i]);
      }
    }

    if (errorMessage) {
      formattedMessage = ReferenceDataService.formatStringWithArguments(errorMessage.Value, validationError.args);

      return formattedMessage;
    }
    errorMessage = this.getConfiguration(ReferenceDataCategoriesConstants.ERROR_MESSAGE, validationError.name).find(
      (error) => error.Code === undefined
    );
    formattedMessage = ReferenceDataService.formatStringWithArguments(errorMessage.Value, validationError.args);

    return formattedMessage;
  }

  public isAdviceJourneyEnabled(): boolean {
    const disableStartAdviceJourneyConfig = this.getClientConfigurationValue(
      ReferenceDataConstants.ClientEnvironmentDisableStartAdviceJourney
    );

    return disableStartAdviceJourneyConfig && disableStartAdviceJourneyConfig.toLowerCase() !== 'y';
  }
}
